import React, {FC} from "react";
import {useTranslation} from "react-i18next";
import {
  Box,
  Button, Card as MuiCard,
  CardActions, CardContent,
  CardMedia as MuiCardMedia, Checkbox, Chip,
  FormControl,
  Grid,
  Popper,
  TextField,
  withStyles
} from "@material-ui/core";
import {Autocomplete} from "@material-ui/lab";
import styled from "styled-components/macro";
import {spacing} from "@material-ui/system";
import {Grade, StarBorder} from "@mui/icons-material";
import {Tooltip} from '@mui/material'
const Card = styled(MuiCard)(spacing);
const CardMedia = styled(MuiCardMedia)`
  height: 155px;
`;

const GradientWrapper = styled.div`
    box-shadow: 0 0 16.9299px rgba(53, 64, 82, 0.05);
    border-radius: 4.83711px;
`;
const GradientText = styled.div`
    background: linear-gradient(90deg, #84FAB0 0%, #8FEAFE 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
`;

const StyledCardContent = withStyles({
  root: {
    backgroundColor: "#2d3a4c",
    padding: "9px",
    paddingTop: "2px"
  }
})(CardContent)


const StyledCardActions = withStyles({
  root: {
    borderTop: "1px solid rgba(236, 236, 236, 0.1)",
    backgroundColor: "#2d3a4c",
    padding: "3px"
  }
})(CardActions)

const StyledFormControl = withStyles({
  root: {
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      display: "none"
    }
  }
})(FormControl)

type RenderImageComponentProps = {
  images: any[]
  onChange: any,
  handleDelete(id: string | number): void
  handleEdit(event: any, id: string | number): void
  handleDownload(files: any, id: string | number): void
  unitStandardValues: any
  onHandleGeneralImage: (id: string) => void
}

const RenderImageComponent: FC<RenderImageComponentProps> = ({
  images,
  onChange,
  handleDelete,
  handleEdit,
  handleDownload,
  unitStandardValues,
  onHandleGeneralImage
}) => {
  const { t } = useTranslation();

  const getChipLabel = (key: string, unitStandardValues: any[]) => {
    let label = ''
    if (unitStandardValues.length > 0) {
      const findValue = unitStandardValues.find((i) => i.key === key)
      if (findValue && findValue !== -1) {
        label = findValue.title
      }
    }
    return label
  }

  const prepareItems = (value: any[]) => {
    if (value.length > 0) {
      return value.map((i) => typeof i === 'string' ? i : i.key)
    }
    return value
  }

  const getOptions = (options: any[], item: any) => {
    if (options?.length > 0) {
      const filterSelected = options.filter((i: any) => !item.items.includes(i.key))
      const addedIsMandatory = filterSelected
        .map((i) => ({
          ...i,
          group_name: i.is_mandatory_image ?
            t("unitImagesDialog_labelMANDATORY") :
            t("unitImagesDialog_labelOPTIONAL")
        }))
      return addedIsMandatory.sort((a,b) => b.is_mandatory_image - a.is_mandatory_image)
    }
    return []
  }

  return (
    <Grid container spacing={6}>
    {images.map((item: any, index: any) => {
      return (
        <Grid item xs={12} sm={5} md={4} key={index}>
          <Box position={'relative'}>
            <Card mb={6}>
              <Box position={'relative'}>
                <CardMedia
                  image={item.thumbnails.small}
                />
                <Tooltip
                  title={'General image'}
                  placement={'top'}
                  PopperProps={{
                    modifiers: [
                      {
                        name: "offset",
                        options: {
                          offset: [0, -15],
                        },
                      },
                    ],
                  }}
                  arrow
                >
                  <Checkbox
                    checked={item.is_general_image}
                    style={{position: 'absolute', top: 0, right: 0}}
                    checkedIcon={<Grade color={'warning'} style={{fontSize: '2rem'}}/>}
                    icon={<StarBorder color={'inherit'} style={{fontSize: '2rem'}}/>}
                    onClick={() => onHandleGeneralImage(item.id)}
                  />
                </Tooltip>
              </Box>
              <StyledCardContent>
                <StyledFormControl fullWidth variant="outlined">
                  <Autocomplete
                    size='small'
                    multiple
                    style={{ marginTop: "9px" }}
                    noOptionsText={"No tags available"}
                    disableClearable
                    PopperComponent={(props) => {
                      return <Popper {...props} className={`${props.className} autocomplete`} />
                    }}
                    value={item.items}
                    onChange={(event, value) => {
                      onChange({items: prepareItems(value), currentImage: item})
                    }}
                    options={getOptions(unitStandardValues, item)}
                    getOptionLabel={(value) => value.title}
                    groupBy={(value) => value.group_name}
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => (
                        <Chip
                          variant="outlined"
                          label={getChipLabel(option, unitStandardValues)}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select type" variant="outlined" />
                    )}
                  />
                </StyledFormControl>
              </StyledCardContent>

              <StyledCardActions>
                <input
                  accept="image/*"
                  type="file"
                  id={`edit-picture-${index}`}
                  onChange={(files) => {
                    handleEdit(files, item.id)
                  }}
                  style={{ display: "none" }}
                >
                </input>
                <label htmlFor={`edit-picture-${index}`}>
                  <Button size="small" color="primary" component="span">
                    <GradientWrapper >
                      <GradientText style={{ fontSize: "14px" }}>
                        {t("generalWords_Edit")}
                      </GradientText>
                    </GradientWrapper>
                  </Button>
                </label>
                <Button size="small" color="primary" onClick={() => handleDownload(images, item.id)}>
                  <GradientWrapper >
                    <GradientText style={{ fontSize: "14px" }}>
                      {t("generalWords_Download")}
                    </GradientText>
                  </GradientWrapper>
                </Button>
                <Button size="small" color="primary" onClick={() => handleDelete(item.id)}>
                  <GradientWrapper >
                    <GradientText style={{ fontSize: "14px" }}>
                      {t("generalWords_Delete")}
                    </GradientText>
                  </GradientWrapper>
                </Button>
              </StyledCardActions>
            </Card>
          </Box>
        </Grid>
      )
    })}
  </Grid>)
}

export default RenderImageComponent